// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Stack, Switch } from '@mantine/core';
import { FC, memo, useContext, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
import ConfigOption from '../../../Atoms/ConfigOption';
import { ThemeText } from '../../../Atoms/ThemeText';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useAddon, useBookitupSubscription } from '../../../Service/Context/BookitupSubscriptionContext';
import { BookitupScope, ProfileShare } from '../../../Service/ProfileS';
import { openTeamAddonMissingModal } from './bookitupTeamAddonModals';
import { BookitupScopeGroup, BookitupScopeOption } from './bookitupTeamScopes';
import { areScopesEqual } from './bookitupTeamUtils';

type TeamPermissionOptionsProps = {
  applicationUserId: number;
  groups: BookitupScopeGroup[];
  isTeamAddonPresent: boolean;
  navigate: NavigateFunction;
  patchShare: (applicationUserId: number, scopes: BookitupScope[]) => Promise<ProfileShare | null>;
  scopes: BookitupScope[];
};

const TeamPermissionOptions: FC<TeamPermissionOptionsProps> = ({
  applicationUserId,
  groups,
  isTeamAddonPresent,
  navigate,
  patchShare,
  scopes,
}) => {
  const [optimisticScopes, setOptimisticScopes] = useState<BookitupScope[] | null>(null);
  const { subscription } = useBookitupSubscription();
  const displayedScopes = optimisticScopes || scopes;

  useEffect(() => {
    if (optimisticScopes && areScopesEqual(optimisticScopes, scopes)) {
      setOptimisticScopes(null);
    }
  }, [optimisticScopes, scopes]);

  const toggleScope = (option: BookitupScopeOption) => {
    if (!isTeamAddonPresent) {
      openTeamAddonMissingModal(navigate, subscription?.interval);
      return;
    }

    const isSelected = displayedScopes.includes(option.scope);
    let updatedScopes = isSelected
      ? displayedScopes.filter((existingScope) => existingScope !== option.scope)
      : [...displayedScopes, option.scope];

    if (!isSelected && option.replacesScope) {
      updatedScopes = updatedScopes.filter((existingScope) => existingScope !== option.replacesScope);
    }

    setOptimisticScopes(updatedScopes);
    patchShare(applicationUserId, updatedScopes).catch(() => setOptimisticScopes(scopes));
  };

  return (
    <>
      {groups.map(({ title, description, options }) => (
        <Stack key={title} spacing={0}>
          <ThemeText size="sm">{description}</ThemeText>
          <Stack spacing="xs" mt="xl">
            {options
              .filter((option) => !option.hiddenWhenSelected || !displayedScopes.includes(option.hiddenWhenSelected))
              .map((option) => (
                <ConfigOption
                  key={option.scope}
                  py={4}
                  title={option.title}
                  description={option.description}
                  rightSection={
                    <Switch checked={displayedScopes.includes(option.scope)} onChange={() => toggleScope(option)} />
                  }
                />
              ))}
          </Stack>
        </Stack>
      ))}
    </>
  );
};

const MemoizedTeamPermissionOptions = memo(
  TeamPermissionOptions,
  (prevProps, nextProps) =>
    prevProps.applicationUserId === nextProps.applicationUserId &&
    prevProps.groups === nextProps.groups &&
    prevProps.isTeamAddonPresent === nextProps.isTeamAddonPresent &&
    prevProps.navigate === nextProps.navigate &&
    prevProps.patchShare === nextProps.patchShare &&
    areScopesEqual(prevProps.scopes, nextProps.scopes),
);

const TeamPermissions: FC<{ groups: BookitupScopeGroup[]; share: ProfileShare }> = ({ groups, share }) => {
  const { bookitupTeam } = useContext(ProfileContext);
  const { patchShare } = bookitupTeam;
  const { isPresent } = useAddon('team');
  const navigate = useNavigate();
  const scopes = share.scopes || [];

  return (
    <MemoizedTeamPermissionOptions
      applicationUserId={share.applicationUserId}
      groups={groups}
      isTeamAddonPresent={isPresent}
      navigate={navigate}
      patchShare={patchShare}
      scopes={scopes}
    />
  );
};

export default TeamPermissions;
